/**
 * SmartSpecificationFormatter Domain Service
 * 
 * Handles intelligent formatting of specification values with:
 * - Date formatting (Norwegian format DD.MM.YYYY)
 * - Unit inference and application
 * - Reference value resolution
 * - Pattern-based formatting
 */

import { DynamicSpecification } from '../value-objects/DynamicSpecification'

/**
 * Service for formatting specification values intelligently
 */
export class SmartSpecificationFormatter {
  /**
   * Format a specification value based on its type and patterns
   */
  format(spec: DynamicSpecification): string {
    let formatted = spec.value

    // Handle date formatting
    if (spec.isDateValue()) {
      formatted = this.formatDate(spec.value)
    }

    // If unit is already present in the spec, use it
    if (spec.unit) {
      return this.addUnit(formatted, spec.unit)
    }

    // Otherwise, infer unit based on spec key patterns
    return this.addInferredUnit(formatted, spec)
  }

  /**
   * Format date to Norwegian format (DD.MM.YYYY)
   */
  private formatDate(value: string): string {
    if (!value) return value

    try {
      const date = new Date(value)
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return value
      }

      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      
      return `${day}.${month}.${year}`
    } catch (error) {
      console.warn('Failed to format date:', value, error)
      return value
    }
  }

  /**
   * Add unit to value if not already present
   */
  private addUnit(value: string, unit: string): string {
    // Check if value already has a unit suffix
    if (this.hasUnitSuffix(value)) {
      return value
    }

    return `${value} ${unit}`
  }

  /**
   * Infer and add unit based on specification key patterns
   */
  private addInferredUnit(value: string, spec: DynamicSpecification): string {
    // Don't add unit if value already has one
    if (this.hasUnitSuffix(value)) {
      return value
    }

    // Handle weight specifications
    if (spec.isWeightSpec()) {
      return this.formatWeight(value, spec.key)
    }

    // Handle angle specifications
    if (spec.isAngleSpec()) {
      return this.formatAngle(value)
    }

    // Handle dimension specifications
    if (spec.isDimensionSpec()) {
      return this.formatDimension(value, spec.key)
    }

    // No inference needed
    return value
  }

  /**
   * Format weight values with appropriate units
   */
  private formatWeight(value: string, key: string): string {
    const lowerKey = key.toLowerCase()

    // WLL and SWL typically use tons
    if (lowerKey === 'wll' || lowerKey === 'swl') {
      return `${value} t`
    }

    // MGW, Tare, Payload, Capacity typically use kg
    if (lowerKey === 'mgw' || lowerKey.includes('tare') || lowerKey.includes('payload') || lowerKey === 'capacity') {
      return `${value} kg`
    }

    // Generic weight
    if (lowerKey.includes('weight')) {
      return `${value} kg`
    }

    return value
  }

  /**
   * Format angle values (add degree symbol)
   */
  private formatAngle(value: string): string {
    // Check if degree symbol already present
    if (value.includes('°')) {
      return value
    }

    return `${value}°`
  }

  /**
   * Format dimension values with appropriate units
   */
  private formatDimension(value: string, key: string): string {
    const lowerKey = key.toLowerCase()

    // Diameter and small dimensions typically use mm
    if (lowerKey.includes('dim') || lowerKey.includes('diameter')) {
      return `${value} mm`
    }

    // Length, height, width typically use m
    if (
      lowerKey.includes('length') ||
      lowerKey.includes('height') ||
      lowerKey.includes('width') ||
      lowerKey.includes('depth')
    ) {
      return `${value} m`
    }

    return value
  }

  /**
   * Check if value already has a unit suffix
   */
  private hasUnitSuffix(value: string): boolean {
    const unitPattern = /\d\s*(kg|t|ton|mm|cm|m|°|deg|degrees)$/i
    return unitPattern.test(value)
  }

  /**
   * Format multiple specifications
   */
  formatMultiple(specs: DynamicSpecification[]): Map<string, string> {
    const formatted = new Map<string, string>()
    
    for (const spec of specs) {
      formatted.set(spec.key, this.format(spec))
    }
    
    return formatted
  }

  /**
   * Format specification with label for display
   */
  formatForDisplay(spec: DynamicSpecification): string {
    const formattedValue = this.format(spec)
    return spec.toDisplayString(formattedValue)
  }

  /**
   * Batch format specifications for display
   */
  formatManyForDisplay(specs: DynamicSpecification[]): string[] {
    return specs.map(spec => this.formatForDisplay(spec))
  }
}
